import { StorageError, uploadData } from "aws-amplify/storage";
import slugify from "slugify";

import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import { isFunction } from "@tanstack/react-table";

import { S3File } from "../types/s3-file";

async function uploadFile({
  fileName,
  file,
  path,
  handleProgress,
}: {
  fileName: string;
  file: File;
  path?: string;
  handleProgress?: (progress: number) => void;
}): Promise<S3File> {
  const uniquePrefix = Math.floor(Date.now() + Math.random());
  const slugifiedFileName = slugify(fileName);
  const response = await uploadData({
    path: `${path ?? ""}${uniquePrefix}_${slugifiedFileName}`,
    data: file,
    options: {
      contentType: file.type,
      onProgress: (progress) => {
        if (isFunction(handleProgress)) {
          handleProgress(
            (progress.transferredBytes / (progress.totalBytes || 1)) * 100,
          );
        }
      },
    },
  }).result;

  return {
    filename: slugifiedFileName,
    key: response.path,
    reference: `https://${import.meta.env.VITE_BUCKET}.s3.${import.meta.env.VITE_AWS_REGION}.amazonaws.com/${path ?? ""}${response.path}`,
  };
}

function useUploadFile(
  mutationOptions?: UseMutationOptions<
    S3File,
    StorageError,
    {
      fileName: string;
      file: File;
      path?: string;
      handleProgress?: (progress: number) => void;
    }
  >,
) {
  return useMutation({
    mutationFn: uploadFile,
    ...mutationOptions,
  });
}

export { useUploadFile };
